import { NextPage } from 'next'
import * as React from 'react'
import { useState, useEffect } from 'react'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import CardMedia from '@mui/material/CardMedia'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import CardActionArea from '@mui/material/CardActionArea'
import { NewsType } from '@/@types'
import Image from 'next/image'
import { APP_NAMESPACE, ARTICLES_BASE_IMG_URL } from '@/constants/AppConfig'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'
import ShareIcon from '@mui/icons-material/Share'
// import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder'
import { styled } from '@mui/system'
import { fToNow } from '@/utils/formatTime'

interface Props {
  post: NewsType
  index: number
}

const StyledCard = styled(Card)(() => ({
  position: 'relative',
  height: '100%', // Set height to 100% to fill the parent container
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden', // Ensure content doesn't overflow
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    // transform: 'translateY(-4px)',
    // boxShadow: theme.shadows[8],
    '& .action-icons': {
      opacity: 1,
    },
  },
}))

const ActionIcons = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(1),
  right: theme.spacing(1),
  display: 'flex',
  gap: theme.spacing(1),
  opacity: 0,
  transition: 'opacity 0.3s ease-in-out',
  zIndex: 2, // Ensure icons are above the image
  [theme.breakpoints.down('sm')]: {
    opacity: 1,
  },
}))

// Styled component for truncated text with proper ellipsis
const TruncatedContent = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'titleLines',
})<{ titleLines?: number }>(({ theme, titleLines = 2 }) => {
  // Card height is fixed, so we can estimate available lines for description
  // Assume title line height is 1.3, description line height is 1.6 (from sx)
  // Total vertical space for content: ~calc(100% - titleHeight - dateHeight - paddings)
  // For simplicity, clamp description lines based on title lines
  // Example: if titleLines = 1, clamp = 7; if 2, clamp = 6; if 3, clamp = 5, etc.
  const clampMap: Record<number, number> = { 1: 8, 2: 7, 3: 6, 4: 5 }
  const clamp = clampMap[titleLines] ?? 4

  return {
    color: theme.palette.text.secondary,
    textAlign: 'justify',
    overflow: 'hidden',
    position: 'relative',
    display: '-webkit-box',
    WebkitLineClamp: clamp,
    WebkitBoxOrient: 'vertical',
    textOverflow: 'ellipsis',
    height: '100%',
    lineHeight: 1.5,
    letterSpacing: '0.015em',
    marginBottom: 0,
    paddingBottom: '1.5em',
    fontSize: '1rem',
    // '&::after': {
    //   content: '"..."',
    //   position: 'absolute',
    //   bottom: 0,
    //   right: 0,
    //   paddingLeft: '4px',
    //   paddingRight: '4px',
    //   backgroundColor: theme.palette.background.paper,
    // },
  }
})

const NewsCard: NextPage<Props> = ({ post, index }: { post: Props['post']; index: Props['index'] }) => {
  const [isClient, setIsClient] = useState(false)
  const [imageError, setImageError] = useState(false)

  // Add error handling for date formatting
  let formattedDate = ''
  try {
    formattedDate = fToNow(new Date(post.createdAt))
  } catch (error) {
    console.error('Error formatting date for post:', post.articleId, error)
  }

  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'))

  // Add error handling for image URL construction
  let imageUrl = `/${APP_NAMESPACE}/images/cover.png`
  try {
    if (post.image && !imageError) {
      imageUrl = `${ARTICLES_BASE_IMG_URL}/${post.image}`
    }
  } catch (error) {
    console.error('Error constructing image URL for post:', post.articleId, error)
    imageUrl = `/${APP_NAMESPACE}/images/cover.png`
  }

  useEffect(() => {
    setIsClient(true)
  }, [])

  // Reset image error state when post changes
  useEffect(() => {
    setImageError(false)
  }, [post.image])

  const handleShare = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation() // Prevent default click behavior on the parent card

    if (!isClient) return // Guard against SSR

    try {
      if (navigator.share) {
        navigator
          .share({
            title: post.title,
            text: post.contentShortSummary,
            url: `${window.location.href}${post.slug}`,
          })
          .catch((error) => console.error('Error sharing:', error))
      } else {
        navigator.clipboard.writeText(`${window.location.href}${post.slug}`).then(() => {
          //alert('Link copied to clipboard!');
        })
      }
    } catch (error) {
      console.error('Error in share functionality:', error)
    }
  }

  const getTitleLines = () => {
    if (!isClient) return 2 // Default for SSR

    try {
      // Roughly estimate characters per line based on screen size
      const charPerLine = window.innerWidth < 600 ? 35 : window.innerWidth < 900 ? 40 : 55
      const lines = Math.ceil(post.title.length / charPerLine)
      // Clamp between 1 and 4 lines for safety
      return Math.max(1, Math.min(lines, 4))
    } catch (error) {
      console.error('Error calculating title lines:', error)
      return 2 // Fallback to default
    }
  }

  return (
    <Card
      sx={{
        height: {
          xs: 465, // mobile
          sm: 465, // small tablets
          md: 485, // tablets
          lg: 485, // desktops
          xl: 485, // large desktops
        },
      }}
      onError={(error) => {
        console.error('Card render error:', error)
      }}
    >
      <StyledCard>
        <CardActionArea component="div" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ position: 'relative', width: '100%', height: 0, paddingTop: '56.25%' }}>
            <CardMedia
              title={post.title || 'Article image'}
              sx={{
                width: '100%',
                height: '100%',
                position: 'absolute',
                top: 0,
                left: 0,
                overflow: 'hidden',
                bgcolor: 'rgba(0,0,0,0.05)', // Light background for empty image areas
              }}
            >
              <Image
                src={imageUrl}
                fill
                sizes="(max-width: 600px) 100vw, (max-width: 900px) 50vw, 33vw"
                alt={post.title || 'Article image'}
                priority={isSmallScreen ? index < 2 : false}
                title={post.title || 'Article image'}
                style={{
                  objectFit: 'cover',
                }}
                onError={() => {
                  // Set error state to trigger fallback image
                  if (!imageError) {
                    setImageError(true)
                  }
                }}
              />
              <ActionIcons className="action-icons">
                {/* <IconButton
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.9)',
                    '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                  }}
                  aria-label="bookmark"
                >
                  <BookmarkBorderIcon fontSize="small" />
                </IconButton> */}
                <IconButton
                  size="small"
                  sx={{
                    backgroundColor: (theme) => theme.palette.background.paper,
                    color: (theme) => theme.palette.text.primary,
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.grey[100],
                    },
                  }}
                  aria-label="share"
                  onClick={handleShare}
                >
                  <ShareIcon fontSize="small" />
                </IconButton>
              </ActionIcons>
            </CardMedia>
          </Box>
          <CardContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              padding: { xs: '12px', sm: '16px' },
              flexGrow: 1,
            }}
          >
            <Typography
              variant="h5"
              component="h2"
              sx={{
                fontWeight: 600,
                marginBottom: 1,
                lineHeight: 1.3,
                fontSize: {
                  xs: '1.25rem',
                  sm: '1.1rem',
                  md: '1.25rem',
                  lg: '1.3rem',
                },
                // display: '-webkit-box',
                // WebkitLineClamp: 2,
                // WebkitBoxOrient: 'vertical',
                // overflow: 'hidden',
              }}
            >
              {post.title || ''}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.caption',
                mb: 1.5,
                fontStyle: 'italic',
                fontSize: '0.75rem',
              }}
            >
              {formattedDate || ''}
            </Typography>
            <Box sx={{ position: 'relative', flexGrow: 1 }}>
              <TruncatedContent variant="body2" titleLines={getTitleLines()}>
                {post.contentShortSummary || ''}
              </TruncatedContent>
            </Box>
          </CardContent>
        </CardActionArea>
      </StyledCard>
    </Card>
  )
}

export default NewsCard
